import React, { useEffect, useState } from "react";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Grid,
  Box,
  Typography,
  FormHelperText,
  Button,
  Stack,
} from "@mui/material";
import FormTemplate from "./FormTemplate";
import { SingleInputTimeRangeField } from "@mui/x-date-pickers-pro/SingleInputTimeRangeField";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import ConditionBuilder from "../components/ConditionBuilder";
import MultiSelectWithSelectAll from "../components/MultiSelectWithAll";
import SelectArrow from "../styles/icons/SelectArrow.jsx";
import { queryTypesFlat } from "../models/nodeTypesConfig.js";
import PipeBuilderMainMenu from "../components/flow/menus/PipeBuilderMainMenu";

const NodesDynamicForm = ({
  formFields,
  title,
  onSubmit,
  onClose,
  initialData = {},
}) => {
  console.log(initialData);
  const [valid, setValid] = useState(false);
  const [touched, setTouched] = useState([]);
  const [conditionGlobalMode, setConditionGlobalMode] = useState("whitelist");
  const COLOR_HEADER = "#1E1E1E";
  const COLOR_ACCENT = "#0DA3E2";
  const COLOR_SURFACE = "#2C2B2D";
  const hasConditionBuilder = formFields.some(
    (field) => field.type === "conditionBuilder"
  );
  const defaultSelectMenuProps = {
    PaperProps: {
      sx: {
        mt: 0.5,
        maxHeight: 300,
        overflowY: "auto",
        bgcolor: COLOR_HEADER,
        backgroundImage: "none",
        color: "#FFFFFF",
        border: `1px solid ${COLOR_ACCENT}`,
        borderRadius: "3px",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: COLOR_HEADER,
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#FFFFFF",
          borderRadius: "3px",
        },
        "& .MuiMenuItem-root": {
          minHeight: 42,
          color: "#FFFFFF",
          fontSize: "14px",
          "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: "rgba(13,163,226,0.22)",
          },
          "&:hover": {
            backgroundColor: "rgba(13,163,226,0.12)",
          },
        },
      },
    },
  };
  const [formData, setFormData] = useState(() => {
    const initialState = formFields.reduce((acc, field) => {
      if (field.type === "checkboxGroup") {
        acc[field.id] = [];
      } else if (field.type === "checkbox") {
        acc[field.id] = field.defaultValue || false;
      } else if (field.type === "conditionBuilder") {
        acc[field.id] = [];
      } else if (field.type === "timeRange") {
        acc[field.id] = null;
      } else if (field.type === "dateTimePicker") {
        acc[field.id] = null;
      } else {
        acc[field.id] = "";
      }
      return acc;
    }, {});

    return {
      ...initialState,
      ...Object.fromEntries(
        Object.entries(initialData).map(([key, value]) => {
          const field = formFields.find((f) => f.id === key);
          if (field && field.type === "timeRange") {
            if (Array.isArray(value) && value.length === 2) {
              const [start, end] = value;
              if (start && end) {
                const startTime = dayjs(start, "HH:mm");
                const endTime = dayjs(end, "HH:mm");

                if (
                  startTime.isValid() &&
                  endTime.isValid() &&
                  endTime.isAfter(startTime)
                ) {
                  return [key, value];
                }
              }
            }
            return [key, null];
          }

          if (Array.isArray(value)) {
            if (value.length > 0 && value[0].hasOwnProperty("value")) {
              return [key, value];
            }
            if (field && field.type === "checkboxGroup") {
              return [key, value];
            }
          }
          return [key, value ?? initialState[key]];
        })
      ),
    };
  });

  const handleTouched = (field) => {
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  const handleChange = (field) => (event) => {
    if (field.type === "checkboxGroup") {
      const { value, checked } = event.target;
      setFormData((prevData) => {
        const currentValues = Array.isArray(prevData[field.id])
          ? prevData[field.id]
          : [];

        const updatedValues = checked
          ? [...currentValues, value]
          : currentValues.filter((v) => v !== value);
        return { ...prevData, [field.id]: updatedValues };
      });
    } else if (field.type === "checkbox") {
      setFormData({
        ...formData,
        [field.id]: event.target.checked,
      });
    } else {
      setFormData({
        ...formData,
        [field.id]: event.target.value,
      });
    }
  };

  const handleSubmit = () => {
    console.log(formData);

    const updatedFormData = { ...formData };

    // Helper function: Convert a raw item (primitive or object)
    // into { value, label } only if needed.
    function coerceToValueLabel(field, item) {
      // If it's already { value, label }, keep it as is.
      if (typeof item === "object" && item.value !== undefined) {
        return item;
      }

      // Otherwise, find the matching option to get a label.
      const option = field.options.find((opt) => opt.value === item);
      return {
        value: item,
        label: option ? option.label : item,
      };
    }

    formFields.forEach((field) => {
      // Only run this logic for selects and checkboxGroups
      if (field.type === "select" || field.type === "checkboxGroup") {
        const currentValue = updatedFormData[field.id];

        // Multi-select or checkboxGroup => array
        if (Array.isArray(currentValue)) {
          updatedFormData[field.id] = currentValue.map((item) =>
            coerceToValueLabel(field, item)
          );
        }
        // Single-select => a single value
        else if (currentValue) {
          updatedFormData[field.id] = coerceToValueLabel(field, currentValue);
        }
      }
    });

    // Pass the updated form data to the parent
    onSubmit(updatedFormData);
  };

  /* ---- conditional field helpers ---- */
  const isFieldDisabled = (field) => {
    if (!field.disabledWhen) return false;
    const dep = formData[field.disabledWhen.field];
    const raw = dep?.value ?? dep; // handle both {value,label} and plain string
    return raw === field.disabledWhen.value;
  };

  const isFieldVisible = (field) => {
    if (!field.visibleWhen) return true;
    const dep = formData[field.visibleWhen.field];
    const raw = dep?.value ?? dep;
    return raw === field.visibleWhen.value;
  };

  const isFormValid = () => {
    return formFields.every((field) => {
      // hidden fields are always valid
      if (!isFieldVisible(field)) return true;
      // disabled fields are always valid
      if (isFieldDisabled(field)) return true;

      const value = formData[field.id];

      if (field.type === "conditionBuilder") {
        return (
          Array.isArray(value) && // must be an array
          value.length > 0 && // at least one row
          value.every(
            (row) =>
              row.type?.toString().trim() && // non-empty type
              row.value?.toString().trim() // non-empty value
          )
        );
      }

      // --- Dedicated check for the Pipe Builder ---
      if (field.type === "pipeBuilder") {
        const isNumericString = (s) =>
          typeof s === "string" && s.trim() !== "" && !Number.isNaN(Number(s));

        const looksLikeDate = (s) => typeof s === "string" && /[-/T:]/.test(s);

        return (
          Array.isArray(value) &&
          value.length > 0 &&
          value.every((row) => {
            const baseOk =
              row.table?.toString().trim() &&
              row.field?.toString().trim() &&
              row.operator?.toString().trim();
            if (!baseOk) return false;

            const op = String(row.operator || "").toLowerCase();

            if (op.includes("between")) {
              if (typeof row.value !== "string") return false;
              const [a = "", b = ""] = row.value.split("|");
              if (!a || !b) return false;

              if (isNumericString(a) && isNumericString(b)) {
                const min = Number(a);
                const max = Number(b);
                return max >= min;
              }

              if (looksLikeDate(a) && looksLikeDate(b)) {
                const s = dayjs(a);
                const e = dayjs(b);
                return (
                  s.isValid() && e.isValid() && (e.isAfter(s) || e.isSame(s))
                );
              }

              return false;
            }

            return String(row.value ?? "").trim().length > 0;
          })
        );
      }

      if (field.type === "timeRange") {
        if (!field.required && (!value || !Array.isArray(value))) {
          return true;
        }

        if (!field.required && Array.isArray(value)) {
          const [start, end] = value;

          if (!start && !end) {
            return true;
          }

          if (!start || !end) {
            return false;
          }

          const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
          if (!timeRegex.test(start) || !timeRegex.test(end)) {
            return false;
          }

          const startTime = dayjs(start, "HH:mm");
          const endTime = dayjs(end, "HH:mm");

          if (!startTime.isValid() || !endTime.isValid()) {
            return false;
          }

          if (!endTime.isAfter(startTime)) {
            return false;
          }

          return true;
        }

        if (field.required) {
          if (!Array.isArray(value) || value.length !== 2) return false;

          const [start, end] = value;
          if (!start || !end) return false;

          const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
          if (!timeRegex.test(start) || !timeRegex.test(end)) return false;

          const startTime = dayjs(start, "HH:mm");
          const endTime = dayjs(end, "HH:mm");

          if (!startTime.isValid() || !endTime.isValid()) return false;

          return endTime.isAfter(startTime);
        }

        return true;
      }

      // --- Existing rules for the other field types ---
      if (field.type === "checkboxGroup") {
        return !field.required
          ? true
          : Array.isArray(value) && value.length > 0;
      }

      if (field.type === "dateTimePicker") {
        if (!field.required) return true;
        const d = dayjs(value);
        return d.isValid() && d.isAfter(dayjs());
      }

      return field.required ? Boolean(value) : true;
    });
  };

  const getTimeRangeError = (fieldId) => {
    const field = formFields.find(
      (f) => f.id === fieldId && f.type === "timeRange"
    );
    if (!field) return null;

    const value = formData[fieldId];

    if ((!value || !Array.isArray(value)) && !field.required) {
      return null;
    }

    if ((!value || !Array.isArray(value)) && field.required) {
      return "Please select a valid time range";
    }

    if (Array.isArray(value) && value.length !== 2) {
      return "Please select a valid time range";
    }

    const [start, end] = value;

    if (!start && !end && !field.required) {
      return null;
    }

    if (!start && !end && field.required) {
      return "Please select both start and end times";
    }

    if (!start || !end) {
      return "Please select both start and end times";
    }

    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(start) || !timeRegex.test(end)) {
      return "Invalid time format";
    }

    const startTime = dayjs(start, "HH:mm");
    const endTime = dayjs(end, "HH:mm");

    if (!startTime.isValid() || !endTime.isValid()) {
      return "Invalid time format";
    }

    if (!endTime.isAfter(startTime)) {
      return "End time must be after start time";
    }

    return null;
  };

  useEffect(() => {
    setValid(isFormValid());
  }, [formData]);

  return (
    <FormTemplate
      title={title}
      handleSubmit={handleSubmit}
      isValid={valid}
      onClose={onClose}
      headerRightAction={
        hasConditionBuilder ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              height: 36,
              border: `1px solid ${COLOR_ACCENT}`,
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <Button
              onClick={() => setConditionGlobalMode("whitelist")}
              sx={{
                minWidth: "121px",
                height: "36px",
                px: "16px",
                py: "6px",
                borderRadius: 0,
                borderRight: `1px solid ${COLOR_ACCENT}`,
                textTransform: "uppercase",
                fontFamily: "Assistant, sans-serif",
                fontWeight: 700,
                fontSize: "16px",
                lineHeight: 1,
                letterSpacing: 0,
                color: "#FFFFFF",
                backgroundColor:
                  conditionGlobalMode === "whitelist"
                    ? COLOR_ACCENT
                    : COLOR_HEADER,
                "&:hover": {
                  backgroundColor:
                    conditionGlobalMode === "whitelist"
                      ? COLOR_ACCENT
                      : COLOR_HEADER,
                  opacity: 0.92,
                },
              }}
            >
              Include All
            </Button>
            <Button
              onClick={() => setConditionGlobalMode("blacklist")}
              sx={{
                minWidth: "114px",
                height: "36px",
                px: "16px",
                py: "6px",
                borderRadius: 0,
                textTransform: "uppercase",
                fontFamily: "Assistant, sans-serif",
                fontWeight: 700,
                fontSize: "16px",
                lineHeight: 1,
                letterSpacing: 0,
                color: "#FFFFFF",
                backgroundColor:
                  conditionGlobalMode === "blacklist"
                    ? COLOR_ACCENT
                    : COLOR_HEADER,
                "&:hover": {
                  backgroundColor:
                    conditionGlobalMode === "blacklist"
                      ? COLOR_ACCENT
                      : COLOR_HEADER,
                  opacity: 0.92,
                },
              }}
            >
              Exclude All
            </Button>
          </Box>
        ) : null
      }
    >
      <Grid
        container
        spacing={2}
        sx={{
          display: "flex",
          alignItems: "stretch",
          justifyContent: "flex-start",
          width: "100%",
          padding: "16px",
          color: "#FFFFFF",
          bgcolor: "#2C2B2D",
        }}
      >
        {formFields.map((field) => {
          if (!isFieldVisible(field)) return null;
          const fieldDisabled = isFieldDisabled(field);

          switch (field.type) {
            case "text":
            case "email":
            case "number":
              return (
                <Grid
                  item
                  direction="column"
                  xs={12}
                  key={field.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    gap: 1,
                    opacity: fieldDisabled ? 0.45 : 1,
                    pointerEvents: fieldDisabled ? "none" : "auto",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: "left",
                      color: "#0DA3E2",
                      fontWeight: 500,
                      mb: 0.5,
                    }}
                  >
                    {field.label}
                  </Typography>
                  <TextField
                    type={field.type}
                    value={formData[field.id]}
                    onChange={handleChange(field)}
                    required={field.required}
                    disabled={fieldDisabled}
                    fullWidth
                    sx={{
                      borderRadius: "7px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "7px",
                        color: "#FFFFFF",
                        bgcolor: COLOR_HEADER,
                        "& fieldset": {
                          borderColor: "#0DA3E2",
                        },
                        "&:hover fieldset": {
                          borderColor: "#0DA3E2",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#0DA3E2",
                        },
                      },
                    }}
                    InputProps={{
                      sx: {
                        height: 56,
                        fontSize: "0.95rem",
                        px: "14px",
                      },
                    }}
                    onBlur={() => handleTouched(field.id)}
                    error={
                      field.required && !formData[field.id] && touched[field.id]
                    }
                    helperText={
                      field.required && touched[field.id] && !formData[field.id]
                        ? "Field is required"
                        : ""
                    }
                  />
                </Grid>
              );
            case "select":
              return (
                <Grid
                  item
                  xs={12}
                  key={field.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    opacity: fieldDisabled ? 0.45 : 1,
                    pointerEvents: fieldDisabled ? "none" : "auto",
                  }}
                >
                  <FormControl
                    fullWidth
                    onBlur={() => handleTouched(field.id)}
                    error={
                      field.required && !formData[field.id] && touched[field.id]
                    }
                    required={field.required}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: "left",
                        color: "#0DA3E2",
                        fontWeight: 500,
                        mb: 0.5,
                      }}
                    >
                      {field.label}
                    </Typography>
                    <Select
                      value={
                        formData[field.id]?.value || formData[field.id] || ""
                      }
                      onChange={handleChange(field)}
                      MenuProps={defaultSelectMenuProps}
                      sx={{
                        borderRadius: "7px",
                        minHeight: 56,
                        height: 56,
                        color: "#FFFFFF",
                        bgcolor: COLOR_HEADER,
                        "& fieldset": {
                          borderColor: "#0DA3E2",
                        },
                        "&:hover fieldset": {
                          borderColor: "#0DA3E2",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#0DA3E2",
                        },
                        "& .MuiSelect-select": {
                          display: "flex",
                          alignItems: "center",
                          px: "14px",
                          py: 0,
                        },
                      }}
                      IconComponent={SelectArrow}
                    >
                      <MenuItem value="">
                        <em style={{ color: "rgba(255,255,255,0.55)" }}>None</em>
                      </MenuItem>
                      {field.options
                        ?.filter(
                          (option) =>
                            option &&
                            option.value != null &&
                            option.label != null
                        )
                        .map((option) => (
                          <MenuItem
                            key={`${field.id}-${option.value}`}
                            value={option.value}
                          >
                            {option.label}
                          </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                      {field.required &&
                      touched[field.id] &&
                      !formData[field.id]
                        ? "Field is required"
                        : ""}
                    </FormHelperText>
                  </FormControl>
                </Grid>
              );
            case "checkbox":
              return (
                <Grid
                  item
                  xs={12}
                  key={field.id}
                  sx={{
                    display: "flex",
                    justifyContent: "left",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData[field.id]}
                        onChange={handleChange(field)}
                      />
                    }
                    label={field.label}
                  />
                </Grid>
              );

            case "checkboxGroup":
              return (
                <Grid
                  item
                  xs={12}
                  key={field.id}
                  sx={{
                    width: "100%",
                    opacity: fieldDisabled ? 0.45 : 1,
                    pointerEvents: fieldDisabled ? "none" : "auto",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.75,
                      mt: 1,
                    }}
                  >
                    {field.options.length === 0 ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          No sources found
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          color="companyBlue"
                          target="_blank"
                          href="https://icreate-campaign.com/admin/Campaigns/CreateCamp?campType="
                          sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            color: "text.primary",
                          }}
                        >
                          Create Campaign
                        </Button>
                      </Stack>
                    ) : (
                      <Box sx={{ width: "854px", maxWidth: "100%" }}>
                        <MultiSelectWithSelectAll
                          label={field.label}
                          options={field.options}
                          value={formData[field.id] || []}
                          onChange={(newValues) => {
                            setFormData((prev) => ({
                              ...prev,
                              [field.id]: newValues,
                            }));
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>
              );

            case "timeRange":
              const timeRangeError = getTimeRangeError(field.id);
              const hasTimeRangeError = Boolean(
                timeRangeError && touched[field.id]
              );

              return (
                <Grid item xs={12} key={field.id}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      mt: 1,
                      width: "100%",
                      opacity: fieldDisabled ? 0.45 : 1,
                      pointerEvents: fieldDisabled ? "none" : "auto",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: "left",
                        color: "text.secondary",
                        fontWeight: 500,
                      }}
                    >
                      {field.label}
                    </Typography>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <SingleInputTimeRangeField
                        value={[
                          formData[field.id]?.[0]
                            ? dayjs(formData[field.id][0], "HH:mm")
                            : null,
                          formData[field.id]?.[1]
                            ? dayjs(formData[field.id][1], "HH:mm")
                            : null,
                        ]}
                        onChange={(newValue) => {
                          setFormData((prev) => ({
                            ...prev,
                            [field.id]: newValue
                              ? [
                                  newValue[0]?.format("HH:mm") || null,
                                  newValue[1]?.format("HH:mm") || null,
                                ]
                              : [null, null],
                          }));
                          handleTouched(field.id);
                        }}
                        onBlur={() => handleTouched(field.id)}
                        format="HH:mm"
                        sx={{
                          width: "100%",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            height: 50,
                          },
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: hasTimeRangeError,
                            helperText:
                              touched[field.id] && timeRangeError
                                ? timeRangeError
                                : "",
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Grid>
              );
            case "conditionBuilder":
              return (
                <Grid item key={field.id}>
                  <ConditionBuilder
                    value={formData[field.id]}
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        //because it ONLY references field id, and we add the fields later, it fails?
                        [field.id]: value,
                      })
                    }
                    conditionTypes={field.conditionTypes}
                    globalMode={conditionGlobalMode}
                    onGlobalModeChange={setConditionGlobalMode}
                    hideGlobalToggle
                  />
                </Grid>
              );
            case "pipeBuilder":
              return (
                <Grid item key={field.id}>
                  <PipeBuilderMainMenu
                    value={formData[field.id]}
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        //because it ONLY references field id, and we add the fields later, it fails?
                        [field.id]: value,
                      })
                    }
                    queryTypes={queryTypesFlat}
                  />
                </Grid>
              );
            case "dateTimePicker":
              const dtValue = formData[field.id]
                ? dayjs(formData[field.id])
                : null;
              const dtError =
                touched[field.id] &&
                field.required &&
                (!dtValue || !dtValue.isValid() || !dtValue.isAfter(dayjs()));

              return (
                <Grid item xs={12} key={field.id}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      mt: 1,
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: "left",
                        color: "text.secondary",
                        fontWeight: 500,
                      }}
                    >
                      {field.label}
                    </Typography>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        value={dtValue}
                        onChange={(newVal) => {
                          setFormData((prev) => ({
                            ...prev,
                            [field.id]: newVal
                              ? newVal.format("YYYY-MM-DDTHH:mm:ssZ")
                              : null,
                          }));
                          handleTouched(field.id);
                        }}
                        disablePast
                        ampm={false}
                        format="DD/MM/YYYY HH:mm"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            onBlur: () => handleTouched(field.id),
                            error: dtError,
                            helperText: dtError
                              ? "Please pick a future date & time"
                              : "",
                            sx: {
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                height: 50,
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Grid>
              );

            default:
              return null;
          }
        })}
      </Grid>
    </FormTemplate>
  );
};

export default NodesDynamicForm;
